import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaBrain as FaBrainBase 
} from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaBrain = FaBrainBase as React.ElementType;

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
}

const RagAgent: React.FC = () => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<Message[]>([
    { id: 1, role: 'assistant', content: 'Hello! I have access to your internal documentation. Ask me anything about the Q3 sales reports or the new HR policies.' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg: Message = { id: Date.now(), role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Simulate RAG Latency
    setTimeout(() => {
      const aiMsg: Message = { 
        id: Date.now() + 1, 
        role: 'assistant', 
        content: `Based on the document "Q3_Financials.pdf", revenue increased by 15% due to the new enterprise tier launch. \n\nSource: Page 12, Paragraph 3.` 
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Left Column: Context */}
          <div>
            <h1 className="text-4xl font-bold text-primary mb-6">Enterprise RAG Agent</h1>
            <p className="text-xl text-secondary mb-8">
              A Retrieval-Augmented Generation system that bridges the gap between Large Language Models and private enterprise data.
            </p>

            <div className="bg-primary p-6 rounded-xl border border-primary mb-8">
              <h3 className="text-lg font-bold text-primary mb-4">How it Works</h3>
              <div className="space-y-6 relative">
                {/* Connecting Line */}
                <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                <div className="relative pl-10">
                  <div className="absolute left-0 top-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center z-10">1</div>
                  <h4 className="font-semibold text-primary">Ingestion</h4>
                  <p className="text-sm text-secondary">PDFs and SQL data are chunked and embedded using Google's `text-embedding-004` or Voyage AI embeddings.</p>
                </div>
                <div className="relative pl-10">
                  <div className="absolute left-0 top-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center z-10">2</div>
                  <h4 className="font-semibold text-primary">Vector Storage</h4>
                  <p className="text-sm text-secondary">Embeddings are stored in Pinecone/ChromaDB for sub-millisecond semantic search.</p>
                </div>
                <div className="relative pl-10">
                  <div className="absolute left-0 top-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center z-10">3</div>
                  <h4 className="font-semibold text-primary">Generation</h4>
                  <p className="text-sm text-secondary">Relevant chunks are injected into the Claude 3.5 Sonnet or Gemini 1.5 Pro context window to generate accurate, sourced answers.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Demo */}
          <div className="h-[600px] flex flex-col bg-primary rounded-xl shadow-2xl border border-primary overflow-hidden">
            <div className="bg-accent p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaBrain />
                <span className="font-semibold">Knowledge Bot</span>
              </div>
              <span className="text-xs bg-white/20 px-2 py-1 rounded">Model: Claude 3.5 Sonnet</span>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/50" ref={scrollRef}>
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-accent text-white rounded-br-none' 
                      : 'bg-primary border border-primary text-primary rounded-bl-none shadow-sm'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-primary border border-primary p-3 rounded-lg rounded-bl-none">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-75"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSend} className="p-4 bg-primary border-t border-primary">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about the data..."
                  className="flex-1 bg-secondary border border-primary rounded-md px-4 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button type="submit" className="bg-accent text-white px-4 py-2 rounded-md hover:opacity-90 transition">
                  Send
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RagAgent;